<script setup lang="ts">
import { computed } from 'vue';

import AppRouterLink from '../appRouterLink/AppRouterLink.vue';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import type { CssCustomPropertyColor } from '../../helpers/cssCustomProperties';

import type { AppBackButtonType } from './AppBackButton.model';

interface AppBackButtonProps {
  type: AppBackButtonType;
  link: string;
  text: string;
  font: 'font-label-m-bold' | 'font-label-m-light';
}

const props = defineProps<AppBackButtonProps>();

interface AppBackButtonEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<AppBackButtonEmits>();

const color = computed<CssCustomPropertyColor>(() =>
  props.type === 'black' ? 'color-soft-black' : 'color-white'
);
</script>

<template>
  <AppRouterLink
    class="app-back-button"
    :class="{ [props.type]: true }"
    :link="props.link"
    :font="props.font"
    :color="color"
    @click="emit('click')"
  >
    <div class="go-back-link">
      <AppSvgIcon name="arrowLeft" size="24" />
      {{ props.text }}
    </div>
  </AppRouterLink>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;

.app-back-button {
  padding: 0 var(--spacing-32);
  height: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  transition: all var(--transition-short);
  text-decoration: unset;

  @include for-less-or-equal-than-899-px {
    padding: 0;
  }

  @include for-less-or-equal-than-1200-px {
    padding: 0;
  }

  @include on-hover {
    cursor: pointer;
    text-decoration: underline;

    background-color: var(--color-neutral-30);
  }

  &.white {
    color: var(--color-white);
  }

  &.black {
    color: var(--color-soft-black);
  }

  .go-back-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-8);
  }
}
</style>
